import { useMemo } from "react";
import get from "lodash/get";

import { Cards } from "~/components/Cards";
import { Displaying, Pagination, Container } from "~/components/Filters";
import { getCheckins } from "~/fetch/checkins";
import { getGroupDefinition } from "~/helpers/groups";
import { capitalizeWords } from "~/helpers/capitalize-words";
import { slugify } from "~/helpers/slugify";
import type { Group, Checkin } from "~/types";

import { Stack } from "./Stack";

type Data = Awaited<ReturnType<typeof getCheckins>>;

const getFilteredText = ({
  grouping,
  group,
  groupValue,
  checkins,
}: {
  grouping: Group | undefined;
  group: string;
  groupValue: string;
  checkins: Checkin[];
}) => {
  if (group === "like") return "Favorite Coffees";
  if (!grouping?.group) return undefined;

  let displayName = grouping.displayName;
  if ("pathPrefix" in grouping && !displayName)
    displayName = grouping.pathPrefix;
  if (!displayName) displayName = slugify(grouping.group);

  let filterValue = groupValue;
  const firstCheckinForGroup = checkins.find(
    (checkin) => slugify(get(checkin, group)) === slugify(groupValue)
  );
  if (firstCheckinForGroup) filterValue = get(firstCheckinForGroup, group);

  return capitalizeWords(`${displayName}: ${filterValue}`);
};

export const CardsPage = ({
  data,
  showAll: showAllProp,
}: {
  data: Data;
  showAll?: boolean;
}) => {
  const showAll =
    showAllProp || !!(data.params.group && data.params.groupValue);

  // const liked = useMemo(() => data.checkins.filter(node => node.like), [data.checkins]);
  const [filteredText, grouping] = useMemo((): [
    string | undefined,
    Group | undefined,
  ] => {
    if (!data.params.group || !data.params.groupValue)
      return [undefined, undefined];

    const grouping = getGroupDefinition(data.params.group);
    const text = getFilteredText({
      grouping,
      group: data.params.group,
      groupValue: data.params.groupValue,
      checkins: data.checkins,
    });

    return [text, grouping];
  }, [data.params.group, data.params.groupValue, data.checkins]);

  return (
    <>
      <Container>
        <Stack>
          <Displaying
            shouts={data.checkins.length}
            checkins={data.totalCheckins}
            countries={data.totalCountries}
            venues={data.totalVenues}
            showAll={showAll}
          />

          {filteredText && <span>Filtering by {filteredText}</span>}

          <Pagination
            page={data.params.page}
            totalPages={data.totalPages}
            prevPage={data.prevPage}
            nextPage={data.nextPage}
          />
        </Stack>
      </Container>

      <Cards
        checkins={data.checkins}
        externalVenueLink={grouping?.group === "venue.name"}
      />

      {data.checkins.length > 10 && (
        <Container>
          <Pagination
            page={data.params.page}
            totalPages={data.totalPages}
            prevPage={data.prevPage}
            nextPage={data.nextPage}
          />
        </Container>
      )}
    </>
  );
};
